import { DocumentMetadata } from "../models";
import { SearchResult } from "../models/search-result";
import { bijlagenPath, documentUrl, hoofddocumentenPath, metadataPath } from "../utils/api-path";
import { getHoofdDocRelatie, isBijlageDocument } from "../utils/Relaties";
import { StringSanitizer } from "../utils/StringSanitizer";
import { BaseClient } from "./baseClient";

const stringSanitizer = new StringSanitizer();

export interface IDocumentMetadataApi {
  createMetadataSession(meta: DocumentMetadata, hoofddocumentId?: string): Promise<{ uploadUrl: string; pid: string }>;
  updateMetadata(
    meta: DocumentMetadata,
    id: string,
    redenId?: string,
    hoofddocumentId?: string
  ): Promise<DocumentMetadata>;
  fetchMetadata(id: string, hoofddocId?: string): Promise<DocumentMetadata>;
  getDocumentBijlages(id: string): Promise<SearchResult[]>;
  depubliceerMetadata(id: string, redenId: string, hoofddocumentId?: string): Promise<any>;
}

export class DocumentMetadataApiV2 extends BaseClient implements IDocumentMetadataApi {
  constructor() {
    super(
      new Headers({
        "Content-type": "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      }),
      process.env.REACT_APP_METADATA_API_ENDPOINT_V2 as string
    );
  }

  createMetadataSession = async (meta: DocumentMetadata, hoofddocumentId?: string) => {
    const pad = hoofddocumentId ? bijlagenPath(hoofddocumentId) : hoofddocumentenPath();

    const { json } = await this.api<DocumentMetadata>("publicaties", pad, "POST", { document: meta.document });
    if (!json) throw new Error("error");

    const pid = json.document.pid as string;
    const id = stringSanitizer.extractIdentifierFromPid(pid);

    return {
      uploadUrl: documentUrl(process.env.REACT_APP_DOCUMENT_API_ENDPOINT_V2 as string, id, hoofddocumentId),
      pid,
    };
  };

  updateMetadata = async (meta: DocumentMetadata, id: string, redenId?: string, hoofddocumentId?: string) => {
    const body = {
      document: meta.document,
      ...(redenId ? { infoWijziging: { redenVerwijderingVervanging: redenId } } : {}),
    };

    const { json } = await this.api<DocumentMetadata>("publicaties", metadataPath(id, hoofddocumentId), "PUT", body);
    if (!json) throw new Error("Error");

    return json;
  };

  fetchMetadata = async (id: string, _hoofddocId?: string) => {
    const { json } = await this.api<DocumentMetadata>("publicaties", id, "GET");
    if (!json) throw new Error("Error");
    return json;
  };

  getDocumentBijlages = async (id: string) => {
    const { json } = await this.api<SearchResult[]>("publicaties", bijlagenPath(id), "GET");
    if (!json) throw new Error("Error");
    return json;
  };

  depubliceerMetadata = async (id: string, redenId: string, hoofddocumentId?: string) => {
    const hoofddocId = hoofddocumentId ?? (await this.zoekHoofddocumentId(id));

    return await this.api<DocumentMetadata>("publicaties", metadataPath(id, hoofddocId), "DELETE", {
      infoVerwijdering: { redenVerwijderingVervanging: redenId },
    });
  };

  private readonly zoekHoofddocumentId = async (id: string): Promise<string | undefined> => {
    const meta = await this.fetchMetadata(id);
    const relaties = meta.documentrelaties ?? [];
    if (!isBijlageDocument(relaties)) return undefined;

    const relatie = getHoofdDocRelatie(relaties);
    if (!relatie) throw new Error("Het hoofddocument van deze bijlage kon niet worden gevonden.");

    return stringSanitizer.extractIdentifierFromPid(relatie);
  };
}
